import { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import formQueryUrl from "../utils/formQueryUrl";
import GetUser from "../utils/getUser";
import UseGraph from "../utils/useGraph";
import {
  COHORTS,
  EMPTY_STATS,
  IOutcome,
  IRenewalStats,
  SEGMENTS,
  share,
} from "./renewal-stats.constants";

const Label = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-light-gray">
    {children}
  </p>
);

/** A single filled rail — how far this cohort got to 100%. */
const Rail = ({ pct, bar }: { pct: number; bar: string }) => (
  <span className="block h-[4px] w-full overflow-hidden rounded-full bg-edge">
    <span
      className={`block h-full rounded-full transition-[width] duration-1000 ease-out ${bar}`}
      style={{ width: `${pct}%` }}
    />
  </span>
);

/** Renewed / lapsed / awaiting, stacked to the cohort's full width. */
const CompositionRail = ({ data }: { data: IOutcome }) => (
  <span className="flex h-[6px] w-full gap-[2px] overflow-hidden rounded-full bg-edge">
    {SEGMENTS.map((s) => (
      <span
        key={s.key}
        className={`h-full rounded-full transition-[width] duration-1000 ease-out ${s.bar}`}
        style={{ width: share(data[s.key], data.total) }}
      />
    ))}
  </span>
);

export default function RenewalStats() {
  const user = GetUser();
  const { startDate, endDate } = UseGraph();
  const isMyCustomer = window.location.pathname === "/my-customers";
  const [loading, setLoading] = useState<boolean>(true);
  const [stats, setStats] = useState<IRenewalStats>(EMPTY_STATS);

  useEffect(() => {
    const findRenewals = async () => {
      setLoading(true);
      const filtersArray = [
        isMyCustomer && { myCustomer: user?.code },
        startDate !== null && { startDate },
        endDate !== null && { endDate },
      ];
      try {
        const res = await axiosInstance.get(
          `/stats/chase/renewals${formQueryUrl(filtersArray, "?")}`
        );
        setStats({ ...EMPTY_STATS, ...res.data });
      } catch (error) {
        console.log(error);
      }
      setLoading(false);
    };

    findRenewals();
  }, [user?.code, isMyCustomer, startDate, endDate]);

  if (loading) {
    return (
      <div className="mt-5 h-[320px] w-full animate-pulse rounded-2xl border border-edge-faint bg-surface-elevated" />
    );
  }

  const { overall } = stats;
  const [whole, decimal] = String(overall.rate.toFixed(1)).split(".");

  return (
    <section className="mt-5 animate-fade-in overflow-hidden rounded-2xl border border-edge bg-surface shadow-card">
      {/* ------------------------------ [ HEADER ] ------------------------------ */}
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 py-4 sm:px-8">
        <Label>Total renewals</Label>
        <p className="text-[12px] font-medium text-light-gray">
          <span className="font-bold tabular-nums text-text-primary">
            {overall.total}
          </span>{" "}
          policies past their renewal date
        </p>
      </div>

      <div className="grid border-t border-edge lg:grid-cols-[minmax(0,42%)_minmax(0,58%)]">
        {/* --------------------------- [ HEADLINE RATE ] -------------------------- */}
        <div className="flex flex-col justify-center px-5 py-8 sm:px-8">
          {/* One gradient across both pieces — the % must not restart it. */}
          <div className="flex items-end gap-1 bg-gradient-heading bg-clip-text text-transparent">
            <span className="text-[68px] font-bold leading-[0.85] tracking-[-0.04em] tabular-nums sm:text-[84px]">
              {whole}
            </span>
            <span className="pb-1 text-[28px] font-bold leading-none tabular-nums sm:text-[34px]">
              .{decimal}%
            </span>
          </div>

          <p className="mt-4 text-[14px] font-medium text-light-gray">
            <span className="font-bold text-text-primary">
              {overall.renewed}
            </span>{" "}
            renewed ·{" "}
            <span className="font-bold text-text-primary">{overall.lapsed}</span>{" "}
            lapsed
            {overall.pending > 0 && (
              <>
                {" "}
                ·{" "}
                <span className="font-bold text-text-primary">
                  {overall.pending}
                </span>{" "}
                awaiting
              </>
            )}
          </p>

          <div className="mt-6 max-w-[420px]">
            <CompositionRail data={overall} />
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
              {SEGMENTS.map((s) => (
                <span
                  key={s.key}
                  className="flex items-center gap-2 text-[11px] font-medium text-light-gray"
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${s.bar}`} />
                  {s.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ------------------------ [ AUTO VS NON-AUTO ] ------------------------- */}
        <div className="border-t border-edge lg:border-l lg:border-t-0">
          {COHORTS.map((cohort, index) => {
            const data = stats[cohort.key];

            return (
              <div
                key={cohort.key}
                className={`px-5 py-6 sm:px-8 ${
                  index ? "border-t border-edge-faint" : ""
                }`}
              >
                <div className="flex items-end justify-between gap-4">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-[13px] font-bold text-text-primary">
                      <span className={`h-2 w-2 rounded-full ${cohort.bar}`} />
                      {cohort.label}
                    </p>
                    <p className="mt-1 truncate text-[12px] font-medium text-light-gray">
                      {cohort.note}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-[30px] font-bold leading-none tabular-nums text-text-primary">
                      {data.rate}
                      <span className="text-[16px] text-light-gray">%</span>
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <Rail pct={data.rate} bar={cohort.bar} />
                  <div className="mt-2 flex justify-between text-[11px] font-medium text-light-gray tabular-nums">
                    <span>
                      <span className="font-bold text-text-primary">
                        {data.renewed}
                      </span>{" "}
                      of {data.total} renewed
                    </span>
                    <span>
                      {data.lapsed} lapsed
                      {data.pending > 0 && ` · ${data.pending} awaiting`}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
